import { Component, OnInit, Input, forwardRef, ElementRef, ViewChild, HostListener, ChangeDetectorRef } from '@angular/core';
import { LocationService } from 'src/app/services/location.service';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, ControlValueAccessor, Validator, AbstractControl, ValidationErrors } from '@angular/forms';
import { SharedService } from 'src/app/services/shared.service';
import { Guid, setTimeout$, Utility } from 'src/app/core/common';
import { concat, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { NgSelectComponent } from '@ng-select/ng-select';
import { PushService } from 'src/app/services/push.service';
import { LocationLevel } from 'src/app/common';

class GeoPoint { Latitude: number; Longitude: number; }

const GLOBE_LABEL = 'سراسر کشور';
const CURRENT_LABEL = 'موقعیت فعلی من!';
@Component({
  selector: 'app-location',
  templateUrl: './location.component.html',
  styleUrls: ['./location.component.scss'],
  styles: [':host{width:100%}'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => LocationComponent),
    multi: true,
  },
  {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => LocationComponent),
    multi: true,
  }]
})
export class LocationComponent implements OnInit, ControlValueAccessor, Validator {
  Utility = Utility;

  constructor(private locationService: LocationService, private sharedService: SharedService, element: ElementRef<HTMLElement>,
    private pushService: PushService, private changeDetectorRef: ChangeDetectorRef) {
    this.required = element.nativeElement.hasAttribute('required');
  }

  popup: boolean = false;
  items$: Observable<any[]>;
  defaults: any[];
  loading = false;
  searchInput$ = new Subject<string>();

  popupId = 'popup-' + Guid.newGuid();
  async ngOnInit() {
    if (Utility.mobile) this.popupSelector = '#' + this.popupId;

    if (!this.sharedService.isBrowser) return;

    this.browseItems = (await this.locationService.getProvinces()).map(_ => Object.assign({ _browse: true }, _));
    await this.loadCities();
    this.setCitiesFilter();
    this.changeDetectorRef.markForCheck();
  }
  browseItems: any[];
  async loadCities() {
    let cities = await this.locationService.getCities();
    // .filter(c => !this.locationService.lastLocations.some(_ => (_.City || _.State) && _.City == c.City && _.State == c.State));
    cities = cities.sort(this.sort);
    // cities.splice(0, 0, ...this.locationService.lastLocations);
    this.defaults = this.browseItems;
    //this.locationService.lastLocations;
    switch (<any>this.acceptLevel) {
      case 'Globe':
      case 0:
      case LocationLevel.Globe:
        this.acceptLevel = LocationLevel.Globe;
        this.cities = [{ City: '', State: '' }, ...cities];
        // this.typeToSearchText = 'شهر  یا استان را جستجو کنید . . .';
        this.defaults.splice(0, 0, { City: '', State: '' });
        break;
      case 'State':
      case 1:
      case LocationLevel.State:
        this.acceptLevel = LocationLevel.State;
        this.cities = cities;
        // this.typeToSearchText = 'شهر یا استان را جستجو کنید . . .';
        break;
      case 'City':
      case 2:
      case LocationLevel.City:
        this.acceptLevel = LocationLevel.City;
        this.cities = cities.filter(_ => !!_.City);
        // this.typeToSearchText = 'شهر را جستجو کنید';
        break;
    }
    if (this.pushService.canLocate) {
      this.cities.splice(0, 0, { current: true });
      this.defaults.splice(0, 0, { current: true });
    }
  }

  setCitiesFilter() {
    // code from ng-select "search and autocomplete" example:
    //  https://stackblitz.com/run?file=src%2Fsearch-autocomplete-example.component.ts
    //  https://ng-select.github.io/ng-select#/search
    this.items$ = concat(
      of(this.defaults), //default items
      this.searchInput$.pipe(
        distinctUntilChanged(),
        tap(() => this.loading = true),
        switchMap(term =>
          this.filterCities(term)
            .pipe(
              catchError(() => of([])), // empty list on error
              tap(() => this.loading = false)
            )
        )
      )
    );
  }
  filterCities(term: string): Observable<any[]> {
    let res: any[] = [];
    if (!term)
      return of(this.defaults);
    else {
      var rightTerms = term.split(' ');
      var wrongTerms = rightTerms.map(_ => Utility.correctWrongKeyword(_));
      res = this.cities.filter(l => [rightTerms, wrongTerms].some(terms => terms.every(t =>
        l.State?.includes(t) ||
        l.City?.includes(t) ||
        l.StateEn?.toLowerCase().includes(t.toLowerCase()) ||
        l.CityEn?.toLowerCase().includes(t.toLowerCase()) ||
        l.Variation?.find(v => v?.includes(t)) ||
        (!l.City && 'استان'.includes(t)) ||
        (this.acceptLevel == LocationLevel.Globe && !l.State && !l.City && GLOBE_LABEL.includes(t))
      )));
      var states = Utility.distinct(
        res.filter(_ => _.State && _.City && !res.find(c => c.State == _.State && !c.City)).map(_ => _.State)
      ).map(_ => { return { State: _ } });
      res.push(...states);
    }

    return of(res.sort((a, b) => this.sort(a, b, term)).slice(0, this.maxSuggest));
  }

  sort(a: any, b: any, term?: string) {
    if (term) {
      if (a.City === term) return -1;
      if (b.City === term) return 1;
    }
    return b.Importance - a.Importance;
  }

  get xModel() {
    if (!this.model || (!this.model.City && !this.model.State && !this.model.GeoPoint)) { return null; }
    return this.model;
  }
  @Input('filter') set filter(filter: any) {
    if (!filter || !this.cities) { return; }
    this.cities = this.cities.filter(_ =>
      (!filter.State || filter.State == _.State) &&
      (!filter.City || filter.City == _.City));
    if (this.cities.length == 1) this.select(this.cities[0]);
  }
  @Input('max-suggest') maxSuggest = 10;
  @Input() searchIcon = false;
  @Input() clearIcon = true;
  @Input('appendResultsTo') popupSelector: string = undefined;

  get supportLocating() { return !!navigator.geolocation; }

  required: boolean;
  model: any;

  cities: any[];
  @Input() placeholder: string;

  // @Output() change = new EventEmitter<string>();
  // @Output() selected = new EventEmitter<any>();

  LocationLevel = LocationLevel;
  @Input('accept-level') acceptLevel: LocationLevel = LocationLevel.State;
  @Input('edit-mode') editMode = true;
  //typeToSearchText = 'شهر یا استان را جستجو کنید';

  nearLocations: any[];

  validate(control: AbstractControl): ValidationErrors {
    if (!this.required
      || this.model.City
      || (this.model.State && [LocationLevel.State, 'State', LocationLevel.Globe, 'Globe'].includes(this.acceptLevel))
      || (this.model.State == '' && this.model.City == '' && [LocationLevel.Globe, 'Globe'].includes(this.acceptLevel))) {
      return null;
    }
    return { required: true };
  }

  writeValue(obj: any): void {
    this.model = obj || { City: '', State: '' };
    this.changeDetectorRef.markForCheck();
  }
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }
  registerOnTouched(fn: any): void { }
  private propagateChange = (_: any) => { };

  @ViewChild('selector') selector: NgSelectComponent;
  @HostListener('focus') focus() {
    this.selector.open();
  }

  trackByFn(item: any) { return item.Id; }

  getTitle(item: any, showSearch?: boolean) {
    if (item.current) return CURRENT_LABEL;
    if (item.City == '' && item.State == '')
      return GLOBE_LABEL;//showSearch ? this.typeToSearchText : GLOBE_LABEL;
    if (!this.getCity(item)) {
      if (item.State) return `استان ${item.State}`;
    } else return `${item.State}، ${item.City}`;
  }

  getCity(location: any) { return location.City == 'استان' ? '' : location.City; }

  get scrollContainer() { return document.querySelector(this.popupSelector); }

  select(location?: any, geo?: GeoPoint) {
    if (location?.current) return this.locate();
    if (location?._back) {
      this.defaults = this.browseItems;
      this.setCitiesFilter();
      this.selector.open();
      setTimeout$(() => { if (this.scrollContainer) this.scrollContainer.scrollTop = 0; });
      return false;
    }
    if (location?._browse) {
      // var defaults = this.acceptLevel == LocationLevel.Globe ? [{ State: '', City: '' }] : [];
      var b = [{ State: '', City: '' }, { _back: true }];
      b.push(...this.cities.filter(_ => _.State == location.State && _.City));
      this.defaults = b;
      this.setCitiesFilter();
      this.selector.open();
      setTimeout$(() => { if (this.scrollContainer) this.scrollContainer.scrollTop = 0; });
      return false;
    }

    if (location) {
      if (!this.model) { this.model = {}; }
      this.model.City = this.getCity(location);
      this.model.State = location.State;
      this.model.GeoPoint = geo || location.Point;

      const items = [...this.locationService.lastLocations];
      const index = items.findIndex(_ => _.City == location.City && _.State == location.State);
      if (index >= 0) { items.splice(index, 1); }
      if (location.City || location.State)
        this.locationService.lastLocations = [location, ...items.slice(0, 4)];
    } else {
      this.model.City = undefined;
      this.model.State = undefined;
      this.model.GeoPoint = undefined;
    }

    this.propagateChange(this.model);
    this.popup = false;
    // this.change.emit(this.model);
    // this.selected.emit(this.model);
  }


  async locate() {
    if (!this.pushService.canLocate) return;
    var location = await this.pushService.requestLocation();
    this.select(location);
  }

  openPopup() {
    this.popup = true;
    setTimeout$(() => { this.selector?.open(); }, 100);

    window.onpopstate = () => this.closePopup();
    history.pushState('modal-opened', null);
  }

  closePopup() {
    this.popup = false;

    window.onpopstate = null;
    history.replaceState('modal-closed', null);
  }

}
